import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './useAuth';
import supabase from '../utils/supabase';

/**
 * Custom hook for managing interview time slots
 * Provides CRUD operations and real-time updates for time slot management
 */
const useTimeSlots = (employerId = null) => {
  const { user } = useAuth();
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const effectiveEmployerId = employerId || user?.id;

  // Load time slots
  const loadTimeSlots = useCallback(async () => {
    if (!effectiveEmployerId) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('interview_time_slots')
        .select('*')
        .eq('employer_id', effectiveEmployerId)
        .eq('is_active', true)
        .order('day_of_week', { ascending: true })
        .order('start_time', { ascending: true });

      if (error) throw error;
      
      setTimeSlots(data || []);
    } catch (err) {
      console.error('Error loading time slots:', err);
      setError('Failed to load time slots');
    } finally {
      setLoading(false);
    }
  }, [effectiveEmployerId]);

  // Create time slot
  const createTimeSlot = useCallback(async (slotData) => {
    if (!effectiveEmployerId) throw new Error('No employer ID available');

    try {
      const { data, error } = await supabase
        .from('interview_time_slots')
        .insert([
          {
            ...slotData,
            employer_id: effectiveEmployerId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        ])
        .select()
        .single();

      if (error) throw error;

      // Update local state optimistically
      setTimeSlots(prev => [...prev, data].sort((a, b) => {
        if (a.day_of_week !== b.day_of_week) {
          return a.day_of_week - b.day_of_week;
        }
        return a.start_time.localeCompare(b.start_time);
      }));

      return { success: true, data };
    } catch (err) {
      console.error('Error creating time slot:', err);
      return { success: false, error: err.message };
    }
  }, [effectiveEmployerId]);

  // Update time slot
  const updateTimeSlot = useCallback(async (slotId, updates) => {
    if (!effectiveEmployerId) throw new Error('No employer ID available');

    try {
      const { data, error } = await supabase
        .from('interview_time_slots')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', slotId)
        .eq('employer_id', effectiveEmployerId)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setTimeSlots(prev => prev.map(slot => 
        slot.id === slotId ? data : slot
      ));

      return { success: true, data };
    } catch (err) {
      console.error('Error updating time slot:', err);
      return { success: false, error: err.message };
    }
  }, [effectiveEmployerId]);

  // Delete time slot (soft delete)
  const deleteTimeSlot = useCallback(async (slotId) => {
    if (!effectiveEmployerId) throw new Error('No employer ID available');

    try {
      // Check for scheduled interviews using this slot
      const { data: scheduledInterviews, error: checkError } = await supabase
        .from('interview_schedules')
        .select('id')
        .eq('time_slot_id', slotId)
        .eq('status', 'scheduled');

      if (checkError) throw checkError;

      if (scheduledInterviews && scheduledInterviews.length > 0) {
        throw new Error('Cannot delete time slot with scheduled interviews');
      }

      const { error } = await supabase
        .from('interview_time_slots')
        .update({ 
          is_active: false, 
          updated_at: new Date().toISOString() 
        })
        .eq('id', slotId)
        .eq('employer_id', effectiveEmployerId);

      if (error) throw error;

      // Update local state
      setTimeSlots(prev => prev.filter(slot => slot.id !== slotId));

      return { success: true };
    } catch (err) {
      console.error('Error deleting time slot:', err);
      return { success: false, error: err.message };
    }
  }, [effectiveEmployerId]);

  // Get available slots for a specific date
  const getAvailableSlots = useCallback(async (date, duration = 60, excludeInterviewId = null) => {
    console.log('getAvailableSlots called with:', { date, duration, excludeInterviewId, effectiveEmployerId });
    
    if (!effectiveEmployerId) {
      console.log('No effectiveEmployerId, returning empty array');
      return [];
    }

    try {
      const dayOfWeek = date.getDay();
      const dateStr = date.toISOString().split('T')[0];
      const now = new Date();
      const isToday = dateStr === now.toISOString().split('T')[0];

      // Get time slots fresh from state to avoid stale closure
      const { data: currentTimeSlots, error: timeSlotsError } = await supabase
        .from('interview_time_slots')
        .select('*')
        .eq('employer_id', effectiveEmployerId)
        .eq('is_active', true)
        .eq('day_of_week', dayOfWeek);

      if (timeSlotsError) throw timeSlotsError;

      // Get configured time slots for this day
      const configuredSlots = currentTimeSlots || [];
      
      if (configuredSlots.length === 0) {
        return [];
      }

      // Get existing interviews for this date
      const { data: existingInterviews, error } = await supabase
        .from('interview_schedules')
        .select('id, scheduled_at, duration_minutes, status')
        .eq('interviewer_id', effectiveEmployerId)
        .gte('scheduled_at', `${dateStr}T00:00:00`)
        .lt('scheduled_at', `${dateStr}T23:59:59`)
        .in('status', ['scheduled', 'confirmed']);

      if (error) throw error;

      // Filter out the interview being rescheduled
      const relevantInterviews = excludeInterviewId 
        ? (existingInterviews || []).filter(interview => interview.id !== excludeInterviewId)
        : (existingInterviews || []);

      // Generate available time slots
      const availableSlots = [];
      
      for (const timeSlot of configuredSlots) {
        const slotStart = new Date(`${dateStr}T${timeSlot.start_time}`);
        const slotEnd = new Date(`${dateStr}T${timeSlot.end_time}`);
        
        // Skip past slots for today
        if (isToday && slotStart < now) continue;
        
        let currentTime = new Date(slotStart);
        const bufferTime = timeSlot.buffer_time_minutes || 15;
        const intervalDuration = Math.max(duration, timeSlot.duration_minutes);
        
        while (currentTime < slotEnd) {
          const intervalEnd = new Date(currentTime.getTime() + intervalDuration * 60000);
          
          if (intervalEnd <= slotEnd) {
            // Count concurrent interviews
            const concurrentCount = relevantInterviews.filter(interview => {
              const interviewStart = new Date(interview.scheduled_at);
              const interviewEnd = new Date(interviewStart.getTime() + (interview.duration_minutes || 60) * 60000);
              
              return (
                (currentTime >= interviewStart && currentTime < interviewEnd) ||
                (intervalEnd > interviewStart && intervalEnd <= interviewEnd) ||
                (currentTime <= interviewStart && intervalEnd >= interviewEnd)
              );
            }).length;

            const isAvailable = concurrentCount < timeSlot.max_concurrent_interviews;

            availableSlots.push({
              start: new Date(currentTime),
              end: new Date(intervalEnd),
              timeSlotId: timeSlot.id,
              available: isAvailable,
              concurrentCount,
              maxConcurrent: timeSlot.max_concurrent_interviews,
              bufferTime: bufferTime,
              slotConfig: timeSlot
            });
          }
          
          // Move to next interval
          currentTime = new Date(currentTime.getTime() + timeSlot.duration_minutes * 60000 + bufferTime * 60000);
        }
      }

      return availableSlots.sort((a, b) => a.start.getTime() - b.start.getTime());
    } catch (err) {
      console.error('Error getting available slots:', err);
      return [];
    }
  }, [effectiveEmployerId]); // Remove timeSlots dependency

  // Calculate statistics
  const stats = useMemo(() => {
    const totalMinutes = timeSlots.reduce((acc, slot) => {
      const [startHour, startMin] = slot.start_time.split(':').map(Number);
      const [endHour, endMin] = slot.end_time.split(':').map(Number);
      const slotMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
      return acc + slotMinutes;
    }, 0);

    const daysActive = [...new Set(timeSlots.map(slot => slot.day_of_week))].length;

    return {
      totalSlots: timeSlots.length,
      totalHours: Math.floor(totalMinutes / 60),
      totalMinutes: totalMinutes % 60,
      daysActive,
      weeklyHours: totalMinutes / 60
    };
  }, [timeSlots]);

  // Set up real-time subscription
  useEffect(() => {
    if (!effectiveEmployerId) return;

    const channel = supabase
      .channel('time-slots-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'interview_time_slots',
          filter: `employer_id=eq.${effectiveEmployerId}`
        },
        () => {
          // Reload time slots when changes occur
          loadTimeSlots();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [effectiveEmployerId, loadTimeSlots]);

  // Load time slots on mount
  useEffect(() => {
    loadTimeSlots();
  }, [loadTimeSlots]);

  // Create a stable getAvailableSlots function that doesn't depend on changing dependencies
  const stableGetAvailableSlots = useCallback(async (date, duration = 60, excludeInterviewId = null, employerId) => {
    
    if (!employerId) {
      return [];
    }

    try {
      const dayOfWeek = date.getDay();
      const dateStr = date.toISOString().split('T')[0];
      const now = new Date();
      const isToday = dateStr === now.toISOString().split('T')[0];

      // Get time slots fresh from database
      const { data: currentTimeSlots, error: timeSlotsError } = await supabase
        .from('interview_time_slots')
        .select('*')
        .eq('employer_id', employerId)
        .eq('is_active', true)
        .eq('day_of_week', dayOfWeek);

      if (timeSlotsError) {
        console.error('Error fetching time slots:', timeSlotsError);
        return [];
      }

      if (!currentTimeSlots || currentTimeSlots.length === 0) {
        return [];
      }

      // Get existing interviews for the date
      const { data: existingInterviews, error: interviewsError } = await supabase
        .from('interview_schedules')
        .select('scheduled_at, duration_minutes')
        .gte('scheduled_at', `${dateStr}T00:00:00`)
        .lt('scheduled_at', `${dateStr}T23:59:59`)
        .neq('status', 'cancelled');

      if (interviewsError) {
        console.error('Error fetching existing interviews:', interviewsError);
        return [];
      }

      const bookedSlots = existingInterviews?.map(interview => {
        const start = new Date(interview.scheduled_at);
        const end = new Date(start.getTime() + interview.duration_minutes * 60000);
        return { start, end };
      }) || [];

      // Generate available slots
      const availableSlots = [];
      const bufferTime = 15; // 15 minutes buffer between interviews

      for (const timeSlot of currentTimeSlots) {
        const [startHour, startMin] = timeSlot.start_time.split(':').map(Number);
        const [endHour, endMin] = timeSlot.end_time.split(':').map(Number);

        let currentTime = new Date(date);
        currentTime.setHours(startHour, startMin, 0, 0);

        const slotEnd = new Date(date);
        slotEnd.setHours(endHour, endMin, 0, 0);

        while (currentTime.getTime() + duration * 60000 <= slotEnd.getTime()) {
          // Skip past times if it's today
          if (isToday && currentTime <= now) {
            currentTime = new Date(currentTime.getTime() + 30 * 60000); // 30-minute intervals
            continue;
          }

          const slotStart = new Date(currentTime);
          const slotEndTime = new Date(currentTime.getTime() + duration * 60000);

          // Check for conflicts with existing interviews
          const hasConflict = bookedSlots.some(booked => {
            return (
              (slotStart >= booked.start && slotStart < booked.end) ||
              (slotEndTime > booked.start && slotEndTime <= booked.end) ||
              (slotStart <= booked.start && slotEndTime >= booked.end)
            );
          });

          availableSlots.push({
            start: slotStart,
            end: slotEndTime,
            available: !hasConflict,
            timeSlotId: timeSlot.id
          });

          // Move to next interval
          currentTime = new Date(currentTime.getTime() + timeSlot.duration_minutes * 60000 + bufferTime * 60000);
        }
      }

      return availableSlots.sort((a, b) => a.start.getTime() - b.start.getTime());
    } catch (err) {
      console.error('Error getting available slots:', err);
      return [];
    }
  }, []); // No dependencies!

  // Memoize operations to prevent infinite re-renders
  const operations = useMemo(() => {
    return {
      loadTimeSlots,
      createTimeSlot,
      updateTimeSlot,
      deleteTimeSlot,
      getAvailableSlots: (date, duration, excludeInterviewId) => 
        stableGetAvailableSlots(date, duration, excludeInterviewId, effectiveEmployerId)
    };
  }, [effectiveEmployerId, loadTimeSlots, createTimeSlot, updateTimeSlot, deleteTimeSlot, stableGetAvailableSlots]);

  return {
    timeSlots,
    loading,
    error,
    stats,
    operations
  };
};

export default useTimeSlots;